import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UploadCloud, Trash2, FileText, Loader2 } from 'lucide-react';
import { API } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Logo } from '@/components/Logo';
import { AdminNav } from '@/components/AdminNav';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'hr', name: 'HR' },
  { id: 'operations', name: 'Operations' },
  { id: 'compliance', name: 'Compliance' },
  { id: 'client-services', name: 'Client Services' },
  { id: 'wealth-advisors', name: 'Wealth Advisors' },
  { id: 'alternative-solutions', name: 'Alternative Solutions' },
];

export default function AdminPage() {
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');

  const load = useCallback(async (category) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents?category=${category}`, {
        withCredentials: true,
      });
      setDocuments(data);
    } catch (e) {
      toast.error('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(activeCategory);
  }, [activeCategory, load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file && !text.trim()) {
      toast.error('Provide either a file or pasted text');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('category', activeCategory);
      if (title) fd.append('title', title);
      if (file) fd.append('file', file);
      if (text.trim()) fd.append('text', text);

      const { data } = await axios.post(`${API}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Indexed "${data.title}" — ${data.chunks} chunks`);
      setTitle('');
      setFile(null);
      setText('');
      // reset native file input
      const fileInput = document.getElementById('admin-file-input');
      if (fileInput) fileInput.value = '';
      load(activeCategory);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, docTitle) => {
    if (!window.confirm(`Delete "${docTitle}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
      toast.success('Document deleted');
      load(activeCategory);
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid={ADMIN.page} className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-4">
          <Link to="/" data-testid={ADMIN.backBtn} className="flex items-center gap-3">
            <Logo variant="mark" className="h-8 w-8 rounded-md" alt="STPK" />
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#0A2540] gap-2 px-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to categories</span>
            </Button>
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
              Administration
            </div>
            <h1 className="font-display text-lg text-[#0A2540] leading-tight">
              Document Management
            </h1>
          </div>
          <div className="ml-auto">
            <AdminNav />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-8">
          <h2 className="font-display text-3xl text-[#0A2540]">Knowledge library</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Upload PDF or text documents into a category. They are chunked, embedded
            locally with <code className="text-[#0A2540] font-mono text-xs">{'all-MiniLM-L6-v2'}</code>{' '}
            and generation is served by Ollama.
          </p>
        </div>

        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 h-auto bg-white dark:bg-[#0F2137] border border-slate-200 dark:border-slate-800 p-1 rounded-md w-full">
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.id}
                value={c.id}
                data-testid={`${ADMIN.categoryTab}-${c.id}`}
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-sm py-2.5 text-sm"
              >
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c.id} value={c.id} className="mt-8 space-y-8">
              {/* Upload card */}
              <section className="bg-white border border-slate-200 rounded-lg p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="h-10 w-10 rounded-md border border-slate-200 flex items-center justify-center">
                    <UploadCloud className="h-5 w-5 text-[#0A2540]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl text-[#0A2540]">
                      Add a document
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Attach a PDF or paste raw text. Assigned to{' '}
                      <span className="font-medium text-[#0A2540]">{c.name}</span>.
                    </p>
                  </div>
                </div>

                <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-600">
                      Title (optional)
                    </Label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      data-testid={ADMIN.uploadTitle}
                      placeholder="e.g. Employee Handbook 2025"
                      className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-600">
                      PDF or text file
                    </Label>
                    <Input
                      id="admin-file-input"
                      type="file"
                      accept=".pdf,.txt,.md"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      data-testid={ADMIN.uploadFile}
                      className="h-11 rounded-md border-slate-300 file:bg-slate-100 file:text-slate-700 file:border-0 file:rounded-md file:px-3 file:py-1.5 file:mr-3 file:text-xs file:uppercase file:tracking-wider file:cursor-pointer"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-600">
                      Or paste text
                    </Label>
                    <Textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      data-testid={ADMIN.uploadText}
                      rows={1}
                      placeholder="Paste content here…"
                      className="min-h-[44px] rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    />
                  </div>

                  <div className="md:col-span-2 pt-2">
                    <Button
                      type="submit"
                      disabled={uploading}
                      data-testid={ADMIN.uploadSubmit}
                      className="h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md px-6 gap-2"
                    >
                      {uploading ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Embedding…</>
                      ) : (
                        <><UploadCloud className="h-4 w-4" /> Upload & Index</>
                      )}
                    </Button>
                  </div>
                </form>
              </section>

              {/* Docs table */}
              <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-xl text-[#0A2540]">
                      {c.name} documents
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {loading ? 'Loading…' : `${documents.length} ${documents.length === 1 ? 'document' : 'documents'} indexed`}
                    </p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">
                        Title
                      </TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">
                        Source
                      </TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">
                        Chunks
                      </TableHead>
                      <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">
                        Uploaded
                      </TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!loading && documents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-16 text-sm text-slate-400">
                          No documents indexed yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {documents.map((d) => (
                      <TableRow
                        key={d.id}
                        data-testid={ADMIN.documentRow}
                        className="border-slate-100 hover:bg-slate-50"
                      >
                        <TableCell className="font-medium text-[#0A2540]">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            {d.title}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {d.source_filename || 'Pasted text'}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm font-mono">
                          {d.chunks}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {new Date(d.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(d.id, d.title)}
                            data-testid={ADMIN.deleteDocBtn}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
